import type { IncomingMessage, ServerResponse } from 'node:http';
import { URL } from 'node:url';
import { applyCorsHeaders } from './cors.js';
import { isAuthorizedShortcutRequest } from './auth.js';
import { matchShortcutRoute } from './router.js';
import { sendShortcutJson } from './response.js';
import type { LocalShortcutServerContext, ShortcutHandlerContext } from './types.js';

function shortcutErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Internal shortcut server error';
}

export function createLocalShortcutRequestHandler(ctx: LocalShortcutServerContext): (
  req: IncomingMessage,
  res: ServerResponse,
) => void {
  return (req, res) => {
    void (async () => {
      try {
        const corsOk = applyCorsHeaders(req, res);
        if (req.method === 'OPTIONS') {
          res.writeHead(corsOk ? 204 : 403);
          res.end();
          return;
        }

        if (!corsOk) {
          sendShortcutJson(res, 403, { error: 'Origin not allowed' });
          return;
        }

        if (req.method !== 'GET') {
          sendShortcutJson(res, 405, { error: 'Method not allowed' });
          return;
        }

        if (!isAuthorizedShortcutRequest(req, ctx.token)) {
          sendShortcutJson(res, 401, { error: 'Unauthorized' });
          return;
        }

        const rawUrl = req.url ?? '/';
        const qIndex = rawUrl.indexOf('?');
        const pathname = qIndex >= 0 ? rawUrl.slice(0, qIndex) : rawUrl;
        const search = qIndex >= 0 ? rawUrl.slice(qIndex) : '';
        const url = new URL(search || '', 'http://local');

        const match = matchShortcutRoute(pathname, req.method);
        if (!match) {
          sendShortcutJson(res, 404, { error: 'Not found' });
          return;
        }

        const handlerCtx: ShortcutHandlerContext = {
          req,
          res,
          params: match.params,
          url,
          workspaceId: ctx.workspaceId,
          sessionWorktreeManager: ctx.sessionWorktreeManager,
          e2ee: ctx.e2ee,
        };

        await match.handler(handlerCtx);
      } catch (err) {
        if (!res.headersSent) {
          sendShortcutJson(res, 500, { error: shortcutErrorMessage(err) });
        }
      }
    })().catch((err: unknown) => {
      if (!res.headersSent) {
        sendShortcutJson(res, 500, { error: shortcutErrorMessage(err) });
      }
    });
  };
}
